import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LanguageShort } from '@app/models/language.models';
import { PossibleLanguages } from '@app/models/tree.models';
import { Variables } from '@app/utils/variables';

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private readonly STORAGE_KEY = 'ta-admin-locale';
  private currentLanguageSubject: BehaviorSubject<LanguageShort>;
  public currentLanguage$: Observable<LanguageShort>;

  constructor(private translateService: TranslateService) {
    const savedLanguage = this.getSavedLanguage();
    this.currentLanguageSubject = new BehaviorSubject<LanguageShort>(savedLanguage);
    this.currentLanguage$ = this.currentLanguageSubject.asObservable();

    this.translateService.setDefaultLang(Variables.DEFAULT_LNG);
    this.translateService.use(savedLanguage);
  }

  get currentLanguage(): LanguageShort {
    return this.currentLanguageSubject.value;
  }

  setLanguage(language: LanguageShort): void {
    this.translateService.use(language);
    this.currentLanguageSubject.next(language);
    this.saveLanguage(language);
  }

  switchLanguage(): void {
    const newLanguage: LanguageShort = this.currentLanguage === 'en' ? 'sr' : 'en';
    this.setLanguage(newLanguage);
  }

  private getSavedLanguage(): LanguageShort {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved === 'en' || saved === 'sr') {
      return saved;
    }
    return Variables.DEFAULT_LNG;
  }

  private saveLanguage(language: LanguageShort): void {
    localStorage.setItem(this.STORAGE_KEY, language);
  }

  translate(key: string, params?: any): string {
    return this.translateService.instant(key, params);
  }

  translateAsync(key: string, params?: any): Observable<string> {
    return this.translateService.get(key, params);
  }

  getPossibleLanguages(): PossibleLanguages[] {
    return [
      { code: 'en', label: 'English' },
      { code: 'sr', label: 'Serbian' },
      { code: 'de', label: 'German' },
      { code: 'fr', label: 'French' },
    ];
  }
}
